import React from "react"
import { Modal, Form, Button, Table } from "react-bootstrap"
import DatePicker from "react-datepicker"

export interface DatePeriodItem {
  id: string
  name: string
  from: string | null
  to: string | null
}

interface DatePeriodItemExt extends DatePeriodItem {
  fromDate: Date | undefined
  toDate: Date | undefined
  valid: boolean
}

interface SetDatePeriodProps {
  show: boolean
  items: DatePeriodItem[]
  onCancel: () => void
  onUpdate: (items: DatePeriodItem[]) => void
}


interface SetDatePeriodState {
  items: DatePeriodItemExt[]
}

export default class SetDatePeriod extends React.PureComponent<SetDatePeriodProps, SetDatePeriodState> {
  state: SetDatePeriodState = { items: [] }

  componentDidMount() {
    this.updateStateWithItems(this.props.items)
  }

  componentDidUpdate(prevProps: SetDatePeriodProps) {
    if (this.props.show && !prevProps.show) {
      this.updateStateWithItems(this.props.items)
    }
  }

  updateStateWithItems = (items: DatePeriodItem[]) => {
    const parseDate = (date: string | null): Date | undefined => !date || isNaN(Date.parse(date)) ? undefined : new Date(date)
    const extItems = items.map(item => ({
      ...item,
      fromDate: parseDate(item.from),
      toDate: parseDate(item.to),
      valid: false
    }))
    extItems.forEach(item => this.setValidity(item))
    this.setState({ items: extItems })
  }

  setValidity = (item: DatePeriodItemExt) => {
    item.valid = (!item.fromDate && !item.toDate) || (!!item.fromDate && !!item.toDate && item.fromDate < item.toDate)
  }

  handleDateChange = (id: string, key: "fromDate" | "toDate", stringKey: "from" | "to", date: Date | null) => {
    const { items } = this.state
    const index = items.findIndex(item => item.id === id)
    if (index >= 0) {
      items[index] = { ...items[index], [key]: date || undefined, [stringKey]: date ? date.toISOString() : null }
      this.setValidity(items[index])
    }
    this.setState({ items: items.map(item => ({ ...item })) })
  }

  render() {
    const { items } = this.state
    const canConfirm = items.every(item => item.valid)
    return (
      <Modal backdrop="static" show={this.props.show} size="xl">
        <Modal.Header>
          <Modal.Title className="text-uppercase">SET DATE FORM</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table className="table-sm dark" borderless responsive>
            <thead>
              <tr>
                <th>Email Address</th>
                <th>Date From</th>
                <th>Date To</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => <tr key={item.id}>
                <td><Form.Control type="text" defaultValue={item.name} readOnly className="bg-white" /></td>
                <td>
                  <DatePicker className="form-control"
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    minDate={new Date()}
                    selected={item.fromDate}
                    enableTabLoop={false}
                    onChange={date => this.handleDateChange(item.id, "fromDate", "from", date as Date)} />
                  {item.toDate && !item.fromDate && <Form.Control.Feedback type="invalid" className="d-block">Date From should be specified</Form.Control.Feedback>}
                </td>
                <td>
                  <DatePicker className="form-control"
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    popperPlacement="top-end"
                    minDate={new Date()}
                    selected={item.toDate}
                    enableTabLoop={false}
                    onChange={date => this.handleDateChange(item.id, "toDate", "to", date as Date)} />
                  {!item.toDate && item.fromDate && <Form.Control.Feedback type="invalid" className="d-block">Date To should be specified</Form.Control.Feedback>}
                  {item.toDate && item.fromDate && item.fromDate >= item.toDate && <Form.Control.Feedback type="invalid" className="d-block">Date To should be later than Date From</Form.Control.Feedback>}
                </td>
              </tr>)}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.props.onCancel}>Cancel</Button>
          <Button onClick={() => this.props.onUpdate(this.state.items)} disabled={!canConfirm} className="ml-1">Confirm</Button>
        </Modal.Footer>
      </Modal>)
  }
}
